import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import { useMixpanel } from '@captaincause/lib/mixpanel'
import { useNextLocale } from '@captaincause/lib/useNextLocale'
import { ProjectPage } from '@captaincause/ui/ProjectPage'
import { tm } from '@captaincause/ui/tm'
import { YoutubeIframe } from '@captaincause/ui/YoutubeIframe'
import { Button } from 'components/Button'
import { useLeftRightArrow } from './useLeftRightArrow'
import type { Campaign, Project } from '@captaincause/types'
import type { ReactNode } from 'react'

type GridLayout = {
  [mediaLength: number]: {
    [mediaOrder: number]: string
  }
}

const GRID_LAYOUTS: GridLayout = [
  {
    2: '1 / 1 / 2 / 2',
    3: '1 / 1 / 2 / 3',
    4: '1 / 1 / 3 / 3',
    5: '1 / 1 / 3 / 3',
  },
  {
    2: '2 / 1 / 3 / 2',
    3: '2 / 1 / 3 / 2',
    4: '3 / 1 / 5 / 2',
    5: '3 / 1 / 4 / 2',
  },
  {
    3: '2 / 2 / 3 / 3',
    4: '3 / 2 / 4 / 3',
    5: '4 / 1 / 5 / 2',
  },
  {
    4: '4 / 2 / 5 / 3',
    5: '3 / 2 / 4 / 3',
  },
  {
    5: '4 / 2 / 5 / 3',
  },
] as const

function getGrid(mediaOrder: number, mediaLength: number) {
  return { gridArea: GRID_LAYOUTS[mediaOrder][mediaLength] }
}

export interface DesktopPanelContainerProps {
  children: ReactNode
  switchProject: (direction: 'left' | 'right') => void
  mixpanelPayload?: { [key: string]: any }
  close: () => void
  project: Project
  campaign?: Campaign
}

export function DesktopPanelContainer({
  children,
  switchProject,
  mixpanelPayload = undefined,
  close,
  project,
  campaign = undefined,
}: DesktopPanelContainerProps) {
  const { t } = useTranslation(['give.page', 'common'])
  const locale = useNextLocale()
  const { track } = useMixpanel()

  const [isInfoVisible, showInfo] = useState(false)
  const infoRef = useRef<HTMLDivElement>(null)

  const [leftArrow, rightArrow] = useLeftRightArrow({ switchProject })

  const medias = [
    project?.youtube_id,
    project?.image,
    project?.image2,
    project?.image3,
    project?.image4,
  ].filter(Boolean)

  useEffect(() => {
    if (isInfoVisible) {
      infoRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      })
    }
  }, [isInfoVisible])

  return (
    <div data-testid="give-panel-desktop">
      <div className="mb-4 flex flex-row justify-between">
        <div className="flex flex-row">
          <Button
            iconLeft="chevron-left"
            ref={leftArrow}
            onClick={() => {
              switchProject('left')
            }}
            variant="raw"
            iconClassName="static"
            className="mr-3 h-8 w-8 rounded-full bg-light-gray p-0"
          />
          <Button
            iconLeft="chevron-right"
            ref={rightArrow}
            variant="raw"
            onClick={() => switchProject('right')}
            iconClassName="static"
            className="h-8 w-8 rounded-full bg-light-gray p-0"
          />
        </div>
        <Button
          iconLeft="cross"
          onClick={close}
          variant="raw"
          iconClassName="static h-5 w-5"
          className="h-8 w-8 rounded-full bg-light-gray p-0"
        />
      </div>
      <div
        className={tm(
          'relative grid grid-cols-2 gap-8 rounded-t-2xl bg-white p-8 pb-10 md:grid-cols-2 lg:grid-cols-[1fr_480px]',
          !isInfoVisible && 'mb-5 rounded-b-2xl pb-8',
        )}
      >
        <div className="relative h-full w-full p-8">
          <div
            className={tm(
              'absolute left-0 top-0 grid h-full w-full gap-1 overflow-hidden rounded-lg ',
              `grid-cols-${medias.length <= 2 ? 1 : 2}`,
              `grid-rows-${medias.length <= 3 ? 2 : 4}`,
            )}
            style={{
              gridTemplateRows:
                medias.length <= 3 ? '50% 50%' : '25% 25% 25% 25%',
            }}
          >
            {medias.map((url, index) => {
              const gridArea = getGrid(index, medias.length)

              return url?.includes('http') ? (
                <Image
                  key={url}
                  src={url || ''}
                  alt=""
                  className={tm('h-full w-full object-cover')}
                  style={gridArea}
                  width={400}
                  height={212}
                />
              ) : (
                <YoutubeIframe
                  key={url}
                  id={url as string}
                  style={gridArea}
                  className="m-0 !h-full max-h-none !w-full rounded-none"
                />
              )
            })}
          </div>
        </div>
        <div className="flex h-full min-h-[600px] w-full flex-col justify-start gap-6 text-left">
          {children}
        </div>
      </div>
      <div
        ref={infoRef}
        className={tm(
          'rounded-b-full',
          isInfoVisible ? 'bg-white' : 'rounded-t-full bg-white',
        )}
      >
        {isInfoVisible ? (
          <>
            <hr className="mx-8 border-gray pb-10" />
            <div>
              <ProjectPage
                as="div"
                project={project}
                className="rounded-b-2xl md:pt-0"
                showImages={false}
                showMicroImpact={false}
                showSimilarProjects={false}
                Link={Button}
                locale={locale}
              />
            </div>
          </>
        ) : (
          <Button
            onClick={() => {
              showInfo(true)
              if (project.id !== undefined) {
                track('Click see more', {
                  project_id: project.id,
                  ...mixpanelPayload,
                })
              }
            }}
            variant="naked"
            iconClassName="mx-3 w-5 h-5"
            className="w-full rounded-full border border-inside bg-light-gray p-4 hover:bg-medium-gray"
          >
            {t('common:see-more')}
          </Button>
        )}
      </div>
    </div>
  )
}
